import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { logError } from "../lib/logError";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [booting, setBooting] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [aiScope, setAiScope] = useState("full");

  const refreshAi = async () => {
    try {
      const { data } = await api.get("/ai-settings");
      setAiEnabled(data.ai_enabled !== false);
      setAiScope(data.ai_scope || (data.ai_enabled === false ? "off" : "full"));
      return data.ai_scope;
    }
    catch { return "full"; }
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setBooting(false));
  }, []);

  useEffect(() => { if (user && user.role) refreshAi(); }, [user]);

  // Flag a fresh login so multi-entity users are shown the entity picker
  // (App.js gate) before entering the workspace. Cleared once they pick.
  // Also STAMP the inactivity clock to "now": a fresh login is unambiguous
  // activity, so SessionGuard must not inherit a stale `claro_session_activity`
  // timestamp from a prior day and instantly time the user out on mount.
  const markFreshLogin = () => {
    try {
      sessionStorage.setItem("claro-entity-unpicked", "1");
      localStorage.setItem("claro_session_activity", String(Date.now()));
    } catch { /* noop */ }
  };

  const login = async (email, password) => {
    setTimedOut(false);
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) return { mfa_required: true, mfa_token: data.mfa_token };
    if (data.mfa_enrollment_required) return { mfa_enrollment_required: true, enroll_token: data.enroll_token, deadline: data.deadline };
    if (data.password_expired) return { password_expired: true, reset_token: data.reset_token };
    markFreshLogin();
    setUser({ ...data.user, mfa_enrollment: data.mfa_enrollment });
    return data.user;
  };

  const verifyMfa = async (mfa_token, { code, backup_code } = {}) => {
    const { data } = await api.post("/auth/mfa/verify", { mfa_token, code: code || "", backup_code: backup_code || "" });
    if (data.password_expired) return { password_expired: true, reset_token: data.reset_token };
    markFreshLogin();
    setUser({ ...data.user, mfa_enrollment: data.mfa_enrollment });
    return data.user;
  };

  const forceResetPassword = async (reset_token, new_password) => {
    const { data } = await api.post("/auth/force-reset-password", { reset_token, new_password });
    markFreshLogin();
    setUser({ ...data.user, mfa_enrollment: data.mfa_enrollment });
    return data.user;
  };

  const enrollMfaSetup = async (enroll_token) => {
    const { data } = await api.post("/auth/mfa/enroll/setup", { enroll_token });
    return data;
  };

  const enrollMfaComplete = async (enroll_token, code) => {
    const { data } = await api.post("/auth/mfa/enroll/complete", { enroll_token, code });
    // NOTE: do NOT setUser here — Login shows the backup codes first, then
    // calls refreshUser() on "Continue" (avoids the <Navigate> guard firing early).
    markFreshLogin();
    return data;
  };

  const refreshUser = async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); return data; } catch { return null; }
  };

  const logout = async (reason = "logout") => {
    try { await api.post("/auth/logout", { reason }); } catch (e) { logError("logout request failed", e); }
    // Clear ALL client-side entity state so a stale active-entity can never carry
    // into the next session (this is what stranded multi-entity users on the wrong
    // company after signing out). NOTE: we intentionally do NOT clear
    // "claro-login-org" here — it captures the company the user is trying to sign
    // into via a vanity URL and is single-use (EntityPicker consumes + clears it),
    // so it must survive a forced sign-out to auto-select the requested company.
    try {
      sessionStorage.removeItem("claro-entity-unpicked");
      localStorage.removeItem("claro_active_registration");
      localStorage.removeItem("claro-view-as-customer");
      localStorage.removeItem("claro_session_activity");
    } catch { /* noop */ }
    setTimedOut(reason === "timeout");
    setUser(false);
  };

  const value = useMemo(() => {
    const isRole = (...roles) => user && roles.includes(user.role);
    return { user, booting, login, verifyMfa, enrollMfaSetup, enrollMfaComplete, forceResetPassword, refreshUser, logout, isRole, investigationRole: user?.investigation_role || null, isInvestigator: !!(user?.investigation_role), hasCipAccess: !!(user?.bcsi_authorized), canEdit: isRole("admin", "analyst", "reviewer"), isSuperadmin: !!(user?.is_superadmin), timedOut, dismissTimeout: () => setTimedOut(false), aiEnabled, setAiEnabled, aiScope, setAiScope, aiConfidentialBlocked: aiScope === "public", refreshAi };
  }, [user, booting, timedOut, aiEnabled, aiScope]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
